import { FC, useId, useState } from 'react'
import { Button, Menu, MenuItem, Popover } from '@mui/material'
import DensityMediumIcon from '@mui/icons-material/DensityMedium'
import FilterListIcon from '@mui/icons-material/FilterList'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import {
  ColumnsPanelTrigger,
  GridColDef,
  GridDensity,
  gridDensitySelector,
  Toolbar,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid'
import { WorklistFilterPreference } from './preferencesSlice'
import { WorklistFilterPanel } from './WorklistFilterPanel'

interface WorklistGridToolbarProps {
  columns: GridColDef[]
  model: WorklistFilterPreference
  onApply: (model: WorklistFilterPreference) => void
}

// Allow the custom toolbar props to be passed through the grid's
// `slotProps.toolbar` in a type-safe way.
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    columns: GridColDef[]
    model: WorklistFilterPreference
    onApply: (model: WorklistFilterPreference) => void
  }
}

const DENSITIES: { value: GridDensity; label: string }[] = [
  { value: 'compact', label: 'Compact' },
  { value: 'standard', label: 'Standard' },
  { value: 'comfortable', label: 'Comfortable' },
]

const DensityMenu = () => {
  const apiRef = useGridApiContext()
  const density = useGridSelector(apiRef, gridDensitySelector)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const menuId = useId()

  return (
    <>
      <Button
        size="small"
        startIcon={<DensityMediumIcon />}
        aria-haspopup="menu"
        aria-expanded={anchorEl != null}
        aria-controls={anchorEl == null ? undefined : menuId}
        onClick={event => setAnchorEl(event.currentTarget)}
      >
        Density
      </Button>
      <Menu
        id={menuId}
        open={anchorEl != null}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        {DENSITIES.map(option => (
          <MenuItem
            key={option.value}
            selected={option.value === density}
            onClick={() => {
              apiRef.current.setDensity(option.value)
              setAnchorEl(null)
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

// Toolbar for the community-grid path: the built-in single-condition filter
// button is replaced by a Filters button that opens the multi-condition panel.
// Columns and density keep the text buttons of the stock community toolbar.
//
// No CSV export control: the grid pages server-side, so a grid-side export covers only
// the loaded page and silently under-reports the worklist. Bulk worklist data comes
// from Looker.
export const WorklistGridToolbar: FC<WorklistGridToolbarProps> = ({ columns, model, onApply }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const activeCount = model.items.length

  return (
    // The grid Toolbar justifies to the end; this toolbar has always sat at the
    // left, under the worklist title, so keep it there.
    <Toolbar style={{ justifyContent: 'flex-start' }}>
      <ColumnsPanelTrigger size="small" startIcon={<ViewColumnIcon />}>
        Columns
      </ColumnsPanelTrigger>
      {/* Inline count rather than a Badge: a floating Badge is clipped by the
          grid root's overflow at the top of the toolbar. */}
      <Button
        size="small"
        startIcon={<FilterListIcon />}
        onClick={event => setAnchorEl(event.currentTarget)}
      >
        Filters{activeCount > 0 ? ` (${activeCount})` : ''}
      </Button>
      <DensityMenu />
      <Popover
        open={anchorEl != null}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <WorklistFilterPanel
          columns={columns}
          model={model}
          onApply={onApply}
          onClose={() => setAnchorEl(null)}
        />
      </Popover>
    </Toolbar>
  )
}
